import { createElement, useCallback, useMemo } from 'react';
import { ROOT_NODE, useEditor, useNode } from '@craftjs/core';
import { useContentComposer } from '../content-composer-provider';
import {
  ComposerSectionMeta,
  ComposerSectionProps,
  SetComposerSectionMeta,
  SetComposerSectionProps,
  UsableComposerSectionProps,
} from '../types';

export const useComposerSection = <
  P extends ComposerSectionProps,
  M extends ComposerSectionMeta = ComposerSectionMeta
>() => {
  const { actions, nodes, query, selectedId } = useEditor((state) => {
    const [firstSelectedId] = state.events.selected;
    return {
      nodes: state.nodes[ROOT_NODE]?.data.nodes ?? [],
      selectedId: firstSelectedId,
    };
  });

  const {
    actions: { setProp, setCustom },
    dom,
    id,
    meta,
    props,
  } = useNode((node) => ({
    dom: node.dom,
    id: node.id,
    meta: node.data.custom as M,
    props: node.data.props as P,
  }));

  const {
    clearSectionValidation,
    flushSectionValidation,
    insertSection,
    isSectionDirty,
    markSectionDirty,
    parts,
    validationErrors: composerValidationErrors,
  } = useContentComposer();
  const sectionIndex = nodes.indexOf(id);
  const isSelected = selectedId === id;

  const validationErrors = useMemo(
    () => composerValidationErrors.filter((error) => error.sectionId === id),
    [composerValidationErrors, id]
  );

  const setProps = useCallback<SetComposerSectionProps<P>>(
    (cb, throttleRate) => {
      setProp(cb as (props: P) => void, throttleRate);
      markSectionDirty(id);
    },
    [id, markSectionDirty, setProp]
  );
  const setMeta = useMemo(() => setCustom as SetComposerSectionMeta<M>, [setCustom]);

  const copySection = useCallback(() => {
    const Part = parts[props.sectionType];
    if (!Part) return;

    // Make a copy
    const element = createElement(Part, props);

    // Insert it after the original or at the top if the original is not found
    insertSection(element, sectionIndex + 1);
  }, [insertSection, parts, props, sectionIndex]);

  const deleteSection = useCallback(() => {
    if (!id || !query.node(id).isDeletable()) return;

    clearSectionValidation(id);
    actions.delete(id);
  }, [actions, clearSectionValidation, id, query]);

  const replaceSection = useCallback(
    (newProps: Partial<UsableComposerSectionProps<P>>) => {
      if (!id) return;

      const thisNode = query.node(id).get();

      if (!thisNode) return;

      const insertIndex = sectionIndex > -1 ? sectionIndex : undefined;

      // Copy this node
      const copy = query.parseFreshNode(thisNode).toNode();

      // Update the copied node's props
      copy.data.props = { ...props, ...newProps };

      // Delete the original node
      clearSectionValidation(id);
      actions.delete(id);

      // Insert the copy into the original's place
      queueMicrotask(() => {
        actions.add(copy, ROOT_NODE, insertIndex);

        // Select the copy if the original was selected
        if (isSelected) {
          actions.selectNode(copy.id);
        }
      });
    },
    [actions, clearSectionValidation, id, isSelected, props, query, sectionIndex]
  );

  return {
    /** Inserts a duplicate of this section immediately after it. */
    copySection,

    /** Deletes this section. */
    deleteSection,

    /** The DOM element for this section. */
    dom,

    /** The instance id for this section. */
    id,

    /** Whether this section is currently selected in the editor. */
    isSelected,

    /** Whether this section has pending scheduled validation work. */
    isValidationDirty: isSectionDirty(id),

    /** The custom metadata for this section. */
    meta,

    /** The props for this section. */
    props,

    /** Immediately validates this section if it is currently dirty. */
    flushSectionValidation: () => flushSectionValidation(id),

    /** Replaces this section with a cloned node merged with the provided props. */
    replaceSection,

    /** Updates this section's custom metadata. */
    setMeta,

    /** Updates this section's props. */
    setProps,

    /** Validation errors associated with this section. */
    validationErrors,
  };
};
